import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const WelcomeSection = () => {
  const benefits = [
    {
      icon: 'Search',
      title: 'Advanced Property Search',
      description: 'Find your perfect property with powerful filters and smart recommendations'
    },
    {
      icon: 'Heart',
      title: 'Save Favorites',
      description: 'Keep track of properties you love and get notified of price changes'
    },
    {
      icon: 'MapPin',
      title: 'Interactive Maps',
      description: 'Explore neighborhoods with detailed maps and local insights'
    },
    {
      icon: 'MessageSquare',
      title: 'Direct Communication',
      description: 'Connect directly with property owners and real estate agents'
    },
    {
      icon: 'TrendingUp',
      title: 'Market Insights',
      description: 'Stay informed with real-time market trends and property valuations'
    },
    {
      icon: 'Shield',
      title: 'Verified Listings',
      description: 'Browse only verified properties from trusted sources'
    }
  ];

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="text-center space-y-4">
        <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto">
          <Icon name="Home" size={32} color="white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Welcome to HireBroker
          </h1>
          <p className="text-lg text-muted-foreground">
            Join thousands of users finding their dream properties
          </p>
        </div>
      </div>
      {/* Featured Image */}
      <div className="relative rounded-2xl overflow-hidden shadow-lg">
        <Image
          src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
          alt="Modern luxury home exterior with beautiful landscaping"
          className="w-full h-64 object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <div className="absolute bottom-4 left-4 text-white">
          <p className="text-sm font-medium">Find Your Dream Home</p>
          <p className="text-xs opacity-90">Start your property journey today</p>
        </div>
      </div>
      {/* Benefits Grid */}
      <div className="space-y-6">
        <h2 className="text-xl font-semibold text-foreground text-center">
          Why Choose HireBroker?
        </h2>
        
        <div className="grid grid-cols-1 gap-4">
          {benefits?.map((benefit, index) => (
            <div key={index} className="flex items-start space-x-3 p-4 bg-card rounded-lg border border-border hover:shadow-md transition-shadow duration-200">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name={benefit?.icon} size={20} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-foreground mb-1">
                  {benefit?.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {benefit?.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Trust Indicators */}
      <div className="bg-muted/50 rounded-xl p-6 space-y-4">
        <div className="text-center">
          <h3 className="font-semibold text-foreground mb-2">Trusted by Property Seekers</h3>
          <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
            <div className="flex items-center space-x-1">
              <Icon name="Users" size={16} />
              <span>50K+ Users</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Home" size={16} />
              <span>10K+ Properties</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Star" size={16} />
              <span>4.8 Rating</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center justify-center space-x-4 pt-2">
          <div className="flex items-center space-x-1">
            <Icon name="Shield" size={16} className="text-success" />
            <span className="text-xs text-muted-foreground">SSL Secured</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="CheckCircle" size={16} className="text-success" />
            <span className="text-xs text-muted-foreground">Verified Listings</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Lock" size={16} className="text-success" />
            <span className="text-xs text-muted-foreground">Privacy Protected</span>
          </div>
        </div>
      </div>
      {/* Success Stories Preview */}
      <div className="space-y-4">
        <h3 className="font-semibold text-foreground text-center">Recent Success Stories</h3>
        <div className="space-y-3">
          {[
            {
              name: "Sarah Johnson",
              location: "New York, NY",
              story: "Found my dream apartment in Manhattan within 2 weeks!",
              avatar: "https://randomuser.me/api/portraits/women/32.jpg"
            },
            {
              name: "Michael Chen",
              location: "Los Angeles, CA",
              story: "Sold my house 20% above asking price through HireBroker.",
              avatar: "https://randomuser.me/api/portraits/men/45.jpg"
            }
          ]?.map((testimonial, index) => (
            <div key={index} className="flex items-start space-x-3 p-3 bg-card rounded-lg border border-border">
              <Image
                src={testimonial?.avatar}
                alt={`${testimonial?.name} profile picture`}
                className="w-10 h-10 rounded-full object-cover"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <p className="font-medium text-sm text-foreground">{testimonial?.name}</p>
                  <span className="text-xs text-muted-foreground">•</span>
                  <p className="text-xs text-muted-foreground">{testimonial?.location}</p>
                </div>
                <p className="text-sm text-muted-foreground italic">"{testimonial?.story}"</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WelcomeSection;
